/**
 * HIPAA Authorization Service Wrapper
 *
 * This file provides the RTK Query hooks for HIPAA authorization operations.
 * It wraps the generated service to prevent issues when regenerating the API client.
 */

import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { baseApiClient as api } from './baseService';

// Define our enhanced API with proper multipart handling
const hipaaAuthorizationApi = api
  .enhanceEndpoints({
    addTagTypes: [
      'hipaa_authorization',
      'hipaa-package', // From hipaaPackageService - for cross-service invalidation
      'package-component', // From hipaaPackageService - for cross-service invalidation
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      hipaaAuthorizationRetrieve: build.query<
        HipaaAuthorizationSchema | null,
        { clientId: string }
      >({
        async queryFn(queryArg, _queryApi, _extraOptions, fetchWithBQ) {
          const result = await fetchWithBQ({
            url: `/api/clients/${queryArg.clientId}/hipaa-authorization/`,
          });

          if (result.error) {
            const error = result.error as FetchBaseQueryError;
            if (typeof error.status === 'number' && error.status === 404) {
              return { data: null };
            }
            return { error: result.error };
          }

          return { data: result.data as HipaaAuthorizationSchema };
        },
        providesTags: ['hipaa_authorization'],
      }),
      hipaaAuthorizationCreateOrUpdate: build.mutation<
        HipaaAuthorizationSchema,
        {
          clientId: string;
          body: {
            file: File;
            signature_date: string;
          };
        }
      >({
        query: (queryArg) => {
          const formData = new FormData();
          formData.append('file', queryArg.body.file);
          formData.append('signature_date', queryArg.body.signature_date);

          return {
            url: `/api/clients/${queryArg.clientId}/hipaa-authorization/`,
            method: 'POST',
            body: formData,
          };
        },
        invalidatesTags: [
          'hipaa_authorization',
          'hipaa-package', // Signature affects package validation
          'package-component', // Components may reference clientHipaaSignedDate
        ],
      }),
      hipaaAuthorizationDestroy: build.mutation<void, { clientId: string }>({
        query: (queryArg) => ({
          url: `/api/clients/${queryArg.clientId}/hipaa-authorization/`,
          method: 'DELETE',
        }),
        invalidatesTags: [
          'hipaa_authorization',
          'hipaa-package', // Removing signature affects package validation
          'package-component', // Components may reference clientHipaaSignedDate
        ],
      }),
    }),
    overrideExisting: true,
  });

// Export the hooks
export const {
  useHipaaAuthorizationRetrieveQuery,
  useHipaaAuthorizationCreateOrUpdateMutation,
  useHipaaAuthorizationDestroyMutation,
} = hipaaAuthorizationApi;

// Export types
export type HipaaAuthorizationSchema = {
  client_id: string;
  signature_date?: string | null;
  id: string;
  signature_url?: string | null;
  cleaned_signature_url?: string | null;
};
